import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import {
    Stack,
    Typography,
    Chip,
    IconButton,
    Icon,
    Tooltip,
    useTheme,
    Grid,
} from '../../../base';
import {
    IEmailTemplate,
    EmailTemplateStatus,
    EmailTemplateType,
    convertIsoDateToMMMDYYYYHHmm,
} from '../../../common';
import {
    StyledCard,
    StyledHeader,
    StyledDivider,
    StyledIconBadge,
    StyledPreview,
    StyledPreviewBody,
    StyledHtmlBody,
    StyledPreviewFooter,
    StyledPargodaLogo,
    StyledActionsBar,
} from './EmailTemplateCard.styles';

interface Props {
    template: IEmailTemplate;
    onEdit: (template: IEmailTemplate) => void;
    onDelete: (template: IEmailTemplate) => void;
}

const EmailTemplateCard = ({ template, onEdit, onDelete }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const isPublished = template.status === EmailTemplateStatus.PUBLISHED;
    const isAutomated = template.template_type === EmailTemplateType.AUTOMATED;
    const title = template.name;
    const editorName = template.last_edited_by_display;
    const formattedDate = convertIsoDateToMMMDYYYYHHmm(template.updated_at);
    const bodyHtml = DOMPurify.sanitize(template.body);

    return (
        <StyledCard>
            <StyledHeader spacing={1.5}>
                <Grid container alignItems="center" gap={1}>
                    <StyledIconBadge>
                        <Icon name="MailOutlineIcon" size={24} />
                    </StyledIconBadge>
                    <Typography
                        variant="h6"
                        color={theme.palette.primary.main}
                        sx={{ flex: 1, fontWeight: 700 }}
                        noWrap
                    >
                        {title}
                    </Typography>
                    <Chip
                        label={t(
                            `settings.emailTemplates.type.${template.template_type}`
                        )}
                        size="small"
                        sx={{
                            backgroundColor: theme.palette.primary.light,
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            border: 'none',
                        }}
                    />
                </Grid>
                <Grid container justifyContent="space-between" gap={1}>
                    {editorName && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                        >
                            {t('settings.emailTemplates.lastEditedBy', {
                                name: editorName,
                            })}
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" noWrap>
                        {t('settings.emailTemplates.lastEdited', {
                            date: formattedDate,
                        })}
                    </Typography>
                </Grid>
            </StyledHeader>
            <StyledDivider />
            <StyledPreview>
                <StyledPreviewBody>
                    <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, display: 'block', mb: 1 }}
                    >
                        {template.subject}
                    </Typography>
                    <StyledHtmlBody
                        variant="caption"
                        dangerouslySetInnerHTML={{ __html: bodyHtml }}
                    />
                </StyledPreviewBody>
                <StyledPreviewFooter
                    container
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                >
                    <StyledPargodaLogo
                        src="/assets/logoIcon.svg"
                        alt="pargoda"
                    />
                    <Typography variant="caption">pargoda.com</Typography>
                </StyledPreviewFooter>
            </StyledPreview>

            <StyledActionsBar
                container
                alignItems="center"
                justifyContent="space-between"
            >
                <Chip
                    label={t(
                        `settings.emailTemplates.status.${template.status}`
                    )}
                    size="small"
                    sx={{
                        backgroundColor: isPublished
                            ? theme.palette.success.light
                            : theme.palette.info.light,
                        color: isPublished
                            ? theme.palette.success.dark
                            : theme.palette.info.main,
                        fontWeight: 500,
                        border: 'none',
                    }}
                />
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Tooltip title={t('settings.emailTemplates.edit')}>
                        <IconButton
                            size="small"
                            onClick={() => onEdit(template)}
                        >
                            <Icon
                                name="EditIcon"
                                size={20}
                                color={theme.palette.secondary.main}
                            />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('settings.emailTemplates.delete')}>
                        <span>
                            <IconButton
                                size="small"
                                disabled={isAutomated}
                                onClick={() => onDelete(template)}
                            >
                                <Icon
                                    name="DeleteIcon"
                                    size={20}
                                    color={
                                        isAutomated
                                            ? theme.palette.action.disabled
                                            : theme.palette.secondary.main
                                    }
                                />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </StyledActionsBar>
        </StyledCard>
    );
};

export default EmailTemplateCard;
